/* =========================================================================
 * THE OUTREACH SEQUENCE PANEL
 *
 * Brian: "there should be a button that allows for a very directed email that
 * is not generic. we should have sequencing tied in here which shows multiple
 * styles of outreach."
 *
 * THE DESIGN DECISION THAT MATTERS: the composed copy is VISIBLE, in full,
 * before anything is sent. The old panel had a "Send campaign" button and no
 * words anywhere on screen — so the only thing a rep could evaluate was
 * whether the button was enabled. You cannot tell that copy is generic if you
 * cannot read it. Showing the real subject and body turns "trust the tool"
 * into "read it and decide", which is the only check that actually catches a
 * bad send.
 *
 * BLOCKED VARIANTS ARE STILL SHOWN. A variant that vanishes because a fact is
 * missing looks like a feature that does not exist. Shown-but-blocked, with
 * the missing fact named, turns a dead end into the next task — and usually
 * that task is "go get the source", which is the thing that improves the
 * record for every other consumer too.
 * ========================================================================= */

import { titleTierFor, titleTierLabel } from '../../lib/titleTierView.js'
import { useState, useMemo } from 'react'
import { buildSequence, sequenceReadiness, dayLabel } from '../../lib/outreachSequence.js'
import { shortMissing } from '../../lib/championToClose.js'
import styles from './championToClose.module.css'

export default function OutreachSequence({ dossier, approvedSurfaces = 0, onSend, onConfirmSent, sentKeys = [], gmail = null, attachmentName = '', draftBusy = false, draftBlocker = '', heldKeys = [], blocked = false, sender = null, recipientMode = 'addressed', recipientNeededKeys = [], draftNotice = '', reportedOutcomes = [], sequence = null, contextKind = 'championship' }) {
  /* Which variants the rep has confirmed they actually sent. Local, because the
   * confirmation is a one-way append to the effort log and re-clicking it would
   * double-count a single send. */
  const [confirmed, setConfirmed] = useState([])
  /* ⚠️ THE DRAFT BUTTON IS DISABLED BEFORE THE CLICK, NOT AFTER.
   *
   * Letting a rep press Draft with no Gmail grant and catching the 409 works, and
   * it teaches the rep that the button is unreliable. Reading the connection here
   * means the control is honest about itself: it names the mailbox it will draft
   * into when it can, and names the thing to fix when it cannot.
   *
   * `gmail === null` is STILL LOADING, not disconnected, so the button stays
   * blocked but says so gently rather than telling the rep to connect an account
   * they may already have connected. */
  const gmailReady = !blocked && !!gmail?.grant?.connected
  const gmailAddress = gmail?.grant?.email || 'your Gmail'
  const gmailBlocker = blocked ? 'Wait until the saved facts have been verified and any correction finishes.' : gmail === null
    ? 'Still checking your Gmail connection.'
    : (gmail?.detail || gmail?.grant?.detail
      || 'Connect your own Gmail above — a draft has to land in your mailbox, not somebody else\'s.')

  const seq = useMemo(
    () => sequence === null ? buildSequence(dossier, { approvedSurfaces, sender }) : sequence,
    [dossier, approvedSurfaces, sender, sequence],
  )
  const readiness = sequenceReadiness(seq)
  const routing = titleTierFor(dossier?.championship)
  // A saved-artwork entry has already obtained explicit current server stages.
  // This only labels its selected original; it never grants stage availability.
  const savedArtworkContext=contextKind==='saved-artwork'&&sequence!==null
  const visibleAttachment=savedArtworkContext||routing.titleTier==='ring_tier'||sequence?.some(stage=>stage.attachmentExpected===true)?attachmentName:''

  // Open the first SENDABLE variant by default. Opening a blocked one first
  // would show an empty body and read as a bug.
  const firstReady = seq.find((s) => s.available)?.key || seq[0]?.key
  const [openKey, setOpenKey] = useState(firstReady)
  const [copied, setCopied] = useState('')

  const copy = async (s) => {
    if(!s.available||s.stageBlocker||blocked||draftBlocker||heldKeys.includes(s.key))return
    try {
      await navigator.clipboard.writeText(`Subject: ${s.subject}\n\n${s.body}`)
      setCopied(s.key)
      setTimeout(() => setCopied(''), 1600)
    } catch {
      setCopied('')
    }
  }

  return (
    <div className={styles.seqWrap}>
      {reportedOutcomes.length>0&&<section aria-label="Recorded send reports">
        {reportedOutcomes.map(report=><div key={report.receipt.id}>
          <button type="button" className="btn" disabled>Send recorded</button>
          <p role="status">{report.label}: rep-reported sent (receipt {report.receipt.id}). This records your report for the saved draft; it does not verify delivery or current mailbox contents.</p>
        </div>)}
      </section>}
      <div className={styles.seqHead}>
        <div>
          <div className={styles.seqTitle}>Outreach sequence · {savedArtworkContext?'Selected saved artwork':titleTierLabel(dossier?.championship)}</div>
          <p className={styles.seqSub} role="status">{visibleAttachment ? 'Saved artwork is available for an unpriced outbound draft.' : routing.titleTierReason}</p>
          <div className={styles.seqSub}>
            {readiness.ready} of {readiness.total} copy previews ready
            {readiness.blocked > 0 && ` · ${readiness.blocked} waiting on the record`}
          </div>
        </div>
      </div>

      {draftNotice && <p role="status">{draftNotice}</p>}
      {blocked && !seq.some(item => item.available) && <button type="button" className="btn btn-primary" disabled title={draftBlocker || gmailBlocker}>Verify saved facts before drafting</button>}
      <div className={styles.seqList}>
        {seq.map((s) => {
          const open = openKey === s.key
          const sent = sentKeys.includes(s.key)
          const recipientNeeded = recipientNeededKeys.includes(s.key)
          return (
            <div
              key={s.key}
              className={`${styles.seqItem} ${open ? styles.seqItemOpen : ''} ${s.available ? '' : styles.seqItemBlocked}`}
            >
              <button
                type="button"
                className={styles.seqRow}
                onClick={() => setOpenKey(open ? '' : s.key)}
                aria-expanded={open}
              >
                <span className={styles.seqDay}>{sequence===null?dayLabel(s.day):s.day===0?'Initial':'Follow-up'}</span>
                <span className={styles.seqLabel}>
                  {s.label}
                  <span className={styles.seqPersona}>{s.persona}</span>
                </span>
                {sent && <span className={styles.seqSent}>{recipientNeeded ? 'Recipient needed' : confirmed.includes(s.key) ? 'send recorded' : 'drafted'}</span>}
                {/* The SHORT form here: a row has one line. The full label,
                    including the sentence that says which control fills the
                    field, is in the panel below when the row is opened. */}
                {!s.available && (
                  <span className={styles.seqBlockedTag} title={s.missing.join(' · ')}>
                    needs {s.missing.map(shortMissing).join(' · ')}
                  </span>
                )}
                <span className={styles.seqChevron}>{open ? '▾' : '▸'}</span>
              </button>

              {open && (
                <div className={styles.seqBody}>
                  {/* WHY THIS ANGLE EXISTS, stated. A rep choosing between six
                      emails needs the reasoning, not just six names — that is
                      what makes this a sequence rather than a template folder. */}
                  <div className={styles.seqAngle}>{s.angle}</div>
                  <div className={styles.seqWhy}>{s.why}</div>

                  {s.available ? (
                    <>
                      <div className={styles.seqField}>
                        <span className={styles.seqFieldLabel}>Subject</span>
                        <div className={styles.seqSubject}>{s.subject}</div>
                      </div>
                      <div className={styles.seqField}>
                        <span className={styles.seqFieldLabel}>Body</span>
                        <pre className={styles.seqPre}>{s.body}</pre>
                      </div>
                      <div className={styles.seqActions}>
                        <p>{visibleAttachment && (s.attachmentExpected===true||s.key!=='congratulations') ? `Attachment: ${visibleAttachment}` : 'No artwork attachment for this touch.'}</p>
                        <button type="button" className="btn" disabled={Boolean(s.stageBlocker)||blocked||Boolean(draftBlocker)||heldKeys.includes(s.key)} title={s.stageBlocker||draftBlocker} onClick={() => copy(s)}>
                          {copied === s.key ? 'Copied' : 'Copy'}
                        </button>
                        {/* ⚠️ THE LABEL USED TO SAY "Send this one" AND IT DID
                            NOT SEND. It called the stager, which writes the copy
                            into the campaign record and puts an email precisely
                            nowhere — then this button turned into a tick reading
                            "Sent". A control that claims a delivery that never
                            happened is worse than no control, because the rep
                            stops watching for the thing that did not occur.

                            Now it says what it does: a draft, into THIS rep's own
                            mailbox, named on the button so there is no doubt
                            whose outbox it lands in. Sending stays a human
                            action in Gmail — nothing here can do it. */}
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={sent || !gmailReady || draftBusy || Boolean(draftBlocker) || Boolean(s.stageBlocker) || heldKeys.includes(s.key)}
                          title={s.stageBlocker || draftBlocker || (gmailReady ? undefined : gmailBlocker)}
                          onClick={() => onSend && onSend(s, recipientMode)}
                        >
                          {sent
                            ? recipientNeeded ? 'Saved - add a recipient in Gmail' : 'Drafted - review it in Gmail'
                            : gmailReady
                              ? draftBusy ? 'Preparing draft…' : recipientMode === 'unaddressed' ? `Approve & save without recipient in ${gmailAddress}` : `Approve & Draft in ${gmailAddress}`
                              : blocked ? 'Verify saved facts before drafting' : 'Connect your Gmail first'}
                        </button>
                        {recipientMode === 'unaddressed' && <p role="status">Recipient needed. This saves an unsent draft with To, Cc and Bcc empty. Add a recipient and review it in Gmail yourself. Existing drafts are not changed.</p>}
                        {draftBlocker && <p role="status">{draftBlocker}</p>}
                        {s.stageBlocker && <p role="status">{s.stageBlocker}</p>}
                        {heldKeys.includes(s.key) && <p role="alert">This draft attempt needs reconciliation. Check the saved draft status before trying again.</p>}
                        {/* ⚠️ STEP 9 NEEDS A BUTTON OR IT IS NOT MEASURED.
                            This service deliberately holds no scope that can
                            read a rep's mail, so it cannot watch the send — the
                            rep is the only witness. Without this control,
                            `outreach.sent` would be a declared event type that
                            nothing ever writes, which is precisely the fault that
                            made approvalRate and meetingRate read 0 for months:
                            a metric with a reader and no writer reports a number
                            rather than an absence. Shown only after a draft
                            exists, because confirming a send for something never
                            drafted is not a thing that can have happened. */}
                        {sent && !recipientNeeded && !reportedOutcomes.some(report=>report.key===s.key) && (
                          <button
                            type="button"
                            className="btn"
                            disabled={confirmed.includes(s.key)}
                            title="Records your report for this saved draft and its recipients. It does not verify mailbox delivery."
                            onClick={() => onConfirmSent && onConfirmSent(s).then(
                              () => setConfirmed((c) => [...c, s.key]),
                              () => {},
                            )}
                          >
                            {confirmed.includes(s.key) ? 'Send recorded' : 'I sent this draft'}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    /* No preview, not even a greyed-out one. A half-composed
                       draft on screen is how placeholder copy gets sent. */
                    <div className={styles.seqMissing}>
                      <p className={styles.seqMissingLead}>
                        Not composed. It needs {s.missing.length === 1 ? 'this' : `these ${s.missing.length}`} before
                        it can say anything specific enough to send:
                      </p>
                      {/* ⚠️ ONE PER LINE, IN FULL. Comma-joining these ran the
                          "how to fix it" clauses into a single sentence that
                          could not be read and could not be acted on. Each
                          line names a missing fact AND where it is set. */}
                      <ul className={styles.seqMissingList}>
                        {s.missing.map((m) => <li key={m}>{m}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

